import { getApiConfig, isMockMode } from '@src/config/api';
import pdfiumWrapper from '@src/modules/pdfium_wrapper';
import store from '@src/store/index';
import type React from 'react';
import { useEffect, useState } from 'react';

/**
 * 画面サイズ変更時のPDF再レンダリングを管理するカスタムフック
 *
 * @param isMobile - モバイル表示かどうか
 * @param isTablet - タブレット表示かどうか
 * @param canvasRef - キャンバス要素への参照
 * @param page - 現在のページ番号
 * @param isRendering - レンダリング中かどうか
 * @param isRirekishoMode - 履歴書モードかどうか
 * @param lastRenderParamsRef - 最後のレンダリングパラメータ参照
 * @param setIsRendering - レンダリング状態更新関数
 * @param setPageTotal - 総ページ数更新関数
 * @param setPdfError - エラー状態更新関数
 * @param onPageTotalChange - 総ページ数変更時のコールバック
 */
export const useScreenSizeSync = (
  isMobile: boolean,
  isTablet: boolean,
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  page: number,
  isRendering: boolean,
  isRirekishoMode: boolean,
  lastRenderParamsRef: React.MutableRefObject<{ url: string; page: number } | null>,
  setIsRendering: (value: boolean) => void,
  setPageTotal: (value: number) => void,
  setPdfError: (value: { type: string; message: string } | null) => void,
  onPageTotalChange?: (total: number) => void
) => {
  const [lastScreenSize, setLastScreenSize] = useState<string>('');

  useEffect(() => {
    const currentScreenSize = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop';

    if (lastScreenSize && lastScreenSize !== currentScreenSize) {
      lastRenderParamsRef.current = null;

      setTimeout(() => {
        if (canvasRef.current && !isRendering) {
          const currentState = store.getState();
          const currentToken = isRirekishoMode
            ? currentState.unifiedPdfAccessUrl.rirekisho.token
            : currentState.unifiedPdfAccessUrl.resume.token;
          const currentPdfUrl = isMockMode()
            ? isRirekishoMode
              ? `${window.location.origin}/rirekisho_example.pdf`
              : `${window.location.origin}/resume_example.pdf`
            : currentToken
              ? isRirekishoMode
                ? `${getApiConfig().baseUrl}/api/v1/pdf/rirekisho/${currentToken}`
                : `${getApiConfig().baseUrl}/api/v1/pdf/resume/${currentToken}`
              : '';

          if (currentPdfUrl) {
            setIsRendering(true);
            const currentParams = { url: currentPdfUrl, page };
            lastRenderParamsRef.current = currentParams;

            pdfiumWrapper(
              canvasRef as React.MutableRefObject<HTMLCanvasElement>,
              currentPdfUrl,
              page,
              (total: number, error?: { type: string; message: string }) => {
                if (total > 0) {
                  setPageTotal(total);
                  if (onPageTotalChange) {
                    onPageTotalChange(total);
                  }
                  setPdfError(null);
                } else if (total === -1) {
                  setPageTotal(0);
                  if (error) {
                    setPdfError(error);
                  } else {
                    setPdfError({
                      type: 'unknown_error',
                      message: 'PDFファイルの処理中にエラーが発生しました',
                    });
                  }
                } else {
                  setPageTotal(1);
                  setPdfError(null);
                }
                setIsRendering(false);
              }
            ).catch((_error: unknown) => {
              setIsRendering(false);
              setPdfError({
                type: 'render_error',
                message: 'PDFのレンダリング中にエラーが発生しました',
              });
            });
          }
        }
      }, 200);
    }

    setLastScreenSize(currentScreenSize);
  }, [
    isMobile,
    isTablet,
    lastScreenSize,
    page,
    isRendering,
    onPageTotalChange,
    isRirekishoMode,
    canvasRef,
    lastRenderParamsRef,
    setIsRendering,
    setPageTotal,
    setPdfError,
  ]);
};
